import type {
  FormStatus,
  IConditionalRule,
  IDynamicForm,
  IFormField,
  IFormSchemaJson,
  IFormStep,
} from "./types";
import { FORM_STATUS } from "./constants";

/** Keep letters, numbers, and underscores; spaces become `_` */
const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

export const buildFormSchemaJson = (form: {
  name: string;
  description: string;
  key: string;
  route: string;
  isTemplate: boolean;
  status: FormStatus;
  isMultiStep: boolean;
  steps: IFormStep[];
  fields: IFormField[];
  conditionalRules: IConditionalRule[];
}): IFormSchemaJson => ({
  key: form.key || slugify(form.name) || "untitled_form",
  route: form.route || "/",
  name: form.name,
  description: form.description,
  isTemplate: form.isTemplate,
  status: form.status,
  isMultiStep: form.isMultiStep,
  steps: form.steps,
  fields: form.fields,
  conditionalLogic: form.conditionalRules,
});

export const createEmptyForm = (): IDynamicForm => {
  const stepId = `s-${Date.now()}`;
  return {
    id: "",
    name: "",
    description: "",
    key: "",
    route: "",
    isTemplate: false,
    status: FORM_STATUS.DRAFT,
    isMultiStep: false,
    steps: [{ id: stepId, title: "", order: 1 }],
    fields: [],
    conditionalRules: [],
    submissionsCount: 0,
    createdAt: new Date().toISOString(),
  };
};

export const cloneFormFromTemplate = (
  template: IDynamicForm,
  overrides?: Partial<IDynamicForm>
): IDynamicForm => {
  const stamp = Date.now();
  const stepMap = new Map(
    template.steps.map((step, index) => [
      step.id,
      { ...step, id: `s-${stamp}-${index + 1}` },
    ])
  );

  const steps = template.steps.map((step, index) => ({
    ...step,
    id: stepMap.get(step.id)?.id ?? `s-${stamp}-${index + 1}`,
  }));

  const fieldMap = new Map<string, string>();
  const fields = template.fields.map((field, index) => {
    const newId = `f-${stamp}-${index + 1}`;
    fieldMap.set(field.id, newId);
    return {
      ...field,
      id: newId,
      stepId: stepMap.get(field.stepId)?.id ?? steps[0]?.id ?? field.stepId,
      options: field.options ? field.options.map((opt) => ({ ...opt })) : undefined,
      validation: field.validation ? { ...field.validation } : undefined,
    };
  });

  const conditionalRules = template.conditionalRules.map((rule, index) => ({
    ...rule,
    id: `r-${stamp}-${index + 1}`,
    triggerFieldId: fieldMap.get(rule.triggerFieldId) ?? rule.triggerFieldId,
  }));

  return {
    ...template,
    id: "",
    name: overrides?.name ?? `${template.name} Copy`,
    description: overrides?.description ?? template.description,
    key: overrides?.key ?? `${template.key}_copy`,
    route: overrides?.route ?? template.route,
    isTemplate: overrides?.isTemplate ?? false,
    status: FORM_STATUS.DRAFT,
    steps,
    fields,
    conditionalRules,
    submissionsCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: undefined,
    ...overrides,
  };
};

/** Unpack API row (list or detail) into UI form shape */
export const mapApiForm = (row: any): IDynamicForm => {
  const schema =
    row?.schema && typeof row.schema === "object" && !Array.isArray(row.schema)
      ? row.schema
      : {};

  return {
    id: String(row.id),
    name: row.name ?? "",
    description: row.description ?? "",
    key: row.key ?? "",
    route: row.route ?? "",
    isTemplate: Boolean(row.isTemplate),
    status: (row.status as FormStatus) || FORM_STATUS.DRAFT,
    isMultiStep: Boolean(schema.isMultiStep),
    steps: Array.isArray(schema.steps) ? schema.steps : [],
    fields: Array.isArray(schema.fields) ? schema.fields : [],
    conditionalRules: Array.isArray(schema.conditionalLogic)
      ? schema.conditionalLogic
      : [],
    submissionsCount: 0,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt ?? undefined,
  };
};

export const buildFormPayload = (form: {
  name: string;
  description: string;
  key: string;
  route: string;
  isTemplate: boolean;
  status: FormStatus;
  isMultiStep: boolean;
  steps: IFormStep[];
  fields: IFormField[];
  conditionalRules: IConditionalRule[];
}) => ({
  key: form.key.trim(),
  name: form.name.trim(),
  description: form.description?.trim() || null,
  route: form.route.trim(),
  isTemplate: form.isTemplate,
  status: form.status,
  schema: buildFormSchemaJson(form),
});
